'use strict';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import isFunction from 'lodash/isFunction';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/combineLatest';
import 'rxjs/add/observable/empty';
import 'rxjs/add/observable/merge';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/throw';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/expand';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/pluck';
import 'rxjs/add/operator/reduce';
import 'rxjs/add/operator/startWith';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/take';
import 'rxjs/add/operator/takeUntil';
import 'rxjs/add/operator/throttleTime';
import {SyncStatus, ViewMode, viewModeFromErrorStatus} from './enums';
import {
  canRequestPage,
  epicErrorHandler,
  expoBackoff,
  getEtagPages,
  getFilterDataFromListState,
  mergeSyncListResults,
  nearestPage,
} from './utils';

export class ReduxListEpics {
  /**
   * @param {ReduxObservableListActions} actions
   * @param {ReduxObservableListOptions} options
   * @param {ReduxObservableListReducers} reducers
   */
  constructor(actions, options, reducers) {
    /** @type {ReduxObservableListActions} **/
    this.actions = actions;
    /** @type {Logger} **/
    this.logger = options.logger;
    /** @type {ReduxObservableListOptions} **/
    this._options = options;
    /** @type {ReduxObservableListReducers} **/
    this._reducers = reducers;

    this.onSyncList = this.onSyncList.bind(this);
    this.onLoadPage = this.onLoadPage.bind(this);
    this.requestPage = this.requestPage.bind(this);
    this.getCacheState = this.getCacheState.bind(this);

    if (options.cacheOptions) {
      this.onInitList = this.onInitList.bind(this);
      this.onListUpdate = this.onListUpdate.bind(this);
      this.epics = [
        this.onInitList,
        this.onListUpdate,
        this.onSyncList,
        this.onLoadPage,
      ];

    } else {
      this.epics = [
        this.onSyncList,
        this.onLoadPage,
      ];
    }

    if (options.useDelete) {
      this.onDeleteItem = this.onDeleteItem.bind(this);
      this.addEpic(this.onDeleteItem);
      this.requestDeleteItem = this.requestDeleteItem.bind(this);
    }

    if (options.useSyncUpdates) {
      this.syncModelUpdates = this.syncModelUpdates.bind(this);
      this.addEpic(this.syncModelUpdates);

      // TODO: fix support for hasParent === true case
      if (isFunction(this._options.isAppReady$) && !this._options.hasParentId) {
        this.onAppReload = this.onAppReload.bind(this);
        this.addEpic(this.onAppReload);
      }
    }
  }

  /**
   * Add a custom epic to the list.
   * @param epic
   */
  addEpic(epic) {
    this.epics.push(epic);
  }

  /**
   * Add custom epics to the list.
   * @param {Array<>} epics
   */
  addEpics(epics) {
    this.epics.push(...epics);
  }

  /**
   * Hydrate a cached list, if available
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {Store} store
   * @return {Observable<ReduxAction>}
   */
  onInitList(action$, store) {
    return action$.ofType(this.actions.initList.TYPE)
      .switchMap(initListAction =>
        this.getCacheState(initListAction)
          .catch(err => {
            this.logger.error({err}, `Failed to retrieve cached state. err=${err && err.message}`);
            return Observable.of(null);
          })
          .takeUntil(action$.ofType(this.actions.destroyList.TYPE))
      )
      .mergeMap(state => {
        if (!state) {
          return Observable.empty();

        } else if (!state.isFiltered) {
          const meta = {};
          if (this._options.hasParentId)
            meta.parentId = state.parentId;

          return Observable
            .merge(
              Observable.of(this.actions.hydrateList(state, meta)),
              this.requestSyncList(store.getState(), state),
            );

        } else {
          // reset filter state
          const options = this._options;
          const baseFilterData = {};
          const meta = {};
          if (this._options.hasParentId)
            meta.parentId = state.parentId;

          if (options.useAutoFilter)
            baseFilterData.filter = this._reducers.initialState.filter;
          if (options.useFilterOptions)
            baseFilterData.filterOptions = this._reducers.initialState.filterOptions;
          if (options.useSortOptions)
            baseFilterData.sortOptions = this._reducers.initialState.sortOptions;

          // Calculate the unfiltered state so that we can skip cycling through the redux
          const unfilteredState = this._reducers.reducer(state, this.actions.applyFilter(baseFilterData, meta));

          return Observable
            .merge(
              Observable.of(this.actions.hydrateList(unfilteredState, meta)),
              this.requestSyncList(store.getState(), unfilteredState),
            );
        }
      })
      .catch(epicErrorHandler(this.logger, 'onInitList'));
  }

  /**
   * Get the cached state for the requested list.
   * @param {ReduxAction<ReduxObservableListState>} initListAction
   * @return {Observable<?ReduxObservableListState>}
   */
  getCacheState(initListAction) {
    if (this._options.cacheOptions) {
      const cacheOptions = this._options.cacheOptions;
      if (this._options.hasParentId) {
        return this.getCachedItem(cacheOptions.getKey(initListAction.payload.parentId));
      } else {
        return this.getCachedItem(cacheOptions.key);
      }

    } else {
      return Observable.of(null);
    }
  }

  getCachedItem(key) {
    const cacheOptions = this._options.cacheOptions;
    return Observable.fromPromise(cacheOptions.localForage.getItem(key).then(state => {
      if (state) {
        return JSON.parse(state, cacheOptions.reviver);
      } else {
        return null;
      }
    }));
  }

  setCachedItem(key, state) {
    const cacheOptions = this._options.cacheOptions;
    return Observable.fromPromise(cacheOptions.localForage.setItem(key, JSON.stringify(state, cacheOptions.replacer)));
  }

  /**
   * When the list is updated, update the cache.
   * CODE_TAG[Store-aware-epic]
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {Store} store
   * @return {Observable<ReduxAction>}
   */
  onListUpdate(action$, store) {
    return action$.ofType(
      this.actions.loadSuccess.TYPE,
      this.actions.loadPageSuccess.TYPE,
      this.actions.syncListSuccess.TYPE,
    )
      .throttleTime(this._options.cacheOptions.throttle, void 0, {leading: true, trailing: true})
      .mergeMap(() => {
        const cacheOptions = this._options.cacheOptions;
        const state = this._options.getState(store.getState());
        if (!state.init
          || (this._options.hasParentId && !state.parentId)
          || (state.mode === ViewMode.LOADING))
          return Observable.empty();

        const key = this._options.hasParentId ? cacheOptions.getKey(state.parentId) : cacheOptions.key;
        return this.setCachedItem(key, state)
          .catch(err => {
            this.logger.error({err}, `Failed to write list to cache with error=${err && err.message}`);
            return Observable.empty()
          });
      })
      .filter(() => false)
      .catch(epicErrorHandler(this.logger, 'onListUpdate'));
  }

  /**
   * Sets up change-detection for automatic refreshing of data.
   * CODE_TAG[Store-aware-epic]
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {Store} store
   * @param {Object} dependencies Optional dependencies injected into epics
   * @return {Observable<ReduxAction>}
   */
  onSyncList(action$, store, dependencies) {
    return action$.ofType(this.actions.initList.TYPE)
      .switchMap(action =>
        this.getCacheState(action)
          .catch(err => {
            this.logger.error({err}, `Failed to retrieve cached state. err=${err && err.message}`);
            return Observable.of(null);
          })
          .map(cachedState => ({
            action,
            cachedState,
          }))
          .takeUntil(action$.ofType(this.actions.destroyList.TYPE))
      )
      .switchMap(({action, cachedState}) =>
        // wait for the first successful page load before watching list items
        // unless the list is already loaded (e.g. from a cache)
        (!cachedState && canRequestPage(this._options.getState(store.getState()))
            ? action$.ofType(this.actions.loadPageSuccess.TYPE).take(1)
            : Observable.of(null))
          .mergeMap(() =>
              Observable.combineLatest(
                // The list has changed, trigger a sync
                this._options.syncNotifier(action.payload.parentId, store, dependencies)
                  .throttleTime(this._options.syncNotifierThrottle, void 0, {leading: true, trailing: true}),
                // Reattempt after a filter change.
                action$.ofType(this.actions.applyFilter.TYPE)
                  .startWith(null)
                  .debounceTime(this._options.syncFilterDebounce),
              )
                .switchMap(() => {
                  const rootState = store.getState();
                  return this.requestSyncList(rootState, this._options.getState(rootState))
                  // Cancel the current sync request if a new filter is applied
                    .takeUntil(action$.ofType(this.actions.applyFilter.TYPE));
                })
            //   .filter(() => false)
          )
          .takeUntil(action$.ofType(this.actions.destroyList.TYPE))
      )
      .catch(epicErrorHandler(this.logger, 'onSyncList'));
  }

  // TODO: improve filter condition then replace all calls to requestSyncList with this method instead
  triggerSyncList(action$, store, overrideState = null) {
    const rootState = store.getState();
    const state = overrideState ? overrideState : this._options.getState(rootState);
    return this.requestSyncList(rootState, state)
    // Cancel the current sync request if a new filter is applied
      .takeUntil(action$.ofType(this.actions.applyFilter.TYPE));
  }

  /**
   * @param rootState
   * @param {ReduxObservableListState} state
   * @returns {*}
   */
  requestSyncList(rootState, state) {
    const etagPages = getEtagPages(this._options, state);
    const filterData = getFilterDataFromListState(this._options, state);
    const totalPages = etagPages.length;
    const meta = {};

    if (this._options.hasParentId) {
      meta.parentId = state.parentId;
    }

    if (totalPages === 0) {
      return this.requestPage(filterData, 0, state.parentId, rootState);
    } else if (totalPages === 1) {
      // single page case
      return this.requestSyncListPage(rootState, state, filterData, etagPages[0])
        .mergeMap(rawResponse => this.handleSyncListResult(this._options.parseListSyncResult(rawResponse), state, meta))
        // TODO: finalize action
        .catch(err => this.actions.loadPageError({err, mode: viewModeFromErrorStatus(err.status)}, meta));

    } else {
      // multiple pages case
      return Observable.of({page: 0, data: {hasMore: true, list: {}, total: 0}})
        .expand(({page, data}) =>
          // Return an empty stream to cancel the Observable
          page === totalPages ? Observable.empty() : this.requestSyncListPage(rootState, state, filterData, etagPages[page], page)
            .map(rawResponse => {
              // merge previous data with current result
              const currData = this._options.parseListSyncResult(rawResponse);
              const nextData = {
                // the last hasMore is only one that matters and intermediate values must all be true.
                hasMore: data.hasMore && currData.hasMore,
                list: mergeSyncListResults(data.list, currData.list),
                total: currData.total,
              };
              return {
                page: page + 1,
                data: nextData,
              };
            })
        )
        .pluck('data')
        // reduce logic is already captured in expand
        .reduce((acc, data) => data, null)
        .mergeMap(data => this.handleSyncListResult(data, state, meta))
        // TODO: finalize action
        .catch(err => this.actions.loadPageError({err, mode: viewModeFromErrorStatus(err.status), meta}));

    }
  }

  /**
   * @param {Object} rootState
   * @param {ReduxObservableListState} state
   * @param {FilterData} filterData
   * @param {ETagData} etagData
   * @param {Number} currentPage
   * @returns {Observable<T>}
   * @private
   */
  requestSyncListPage(rootState, state, filterData, etagData, currentPage = 0) {
    if (etagData.size === 0)
      return Observable.empty();

    const syncListRequest = {
      currentPage,
      pageSize: nearestPage(etagData.size, this._options.pageSize, this._options.syncPageSize),
      filterData,
      body: {
        list: etagData.etags,
      }
    };

    if (this._options.hasParentId) {
      syncListRequest.parentId = state.parentId;
    }

    return this._options.syncListFactory.sendRequest(syncListRequest, rootState)
      .catch(expoBackoff(this.logger, 'requestSyncList',
        err => Observable.throw(err),
        (err, errorCount) => errorCount < 5 && isRecoverable(err.status),
      ));
  }

  /**
   * @param result
   * @param {ReduxObservableListState} state Must be the state at the time the request was made.
   * @param meta
   * @returns {*}
   */
  handleSyncListResult(result, state, meta) {
    const successPayload = {
      isFiltered: state.isFiltered,
      currentPage: state.isFiltered ? state.currentFilteredPage : state.currentPage,
      hasMore: result.hasMore,
      list: result.list,
      total: result.total,
    };

    return Observable.of(this.actions.syncListSuccess(successPayload, meta));
  }

  /**
   * Handles loading pages.
   * CODE_TAG[Stateful-epic]
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {Store} store
   * @return {Observable<R>}
   */
  onLoadPage(action$, store) {
    return action$.ofType(this.actions.initList.TYPE)
      .switchMap(initListAction =>
        // check for available cached data
        this.getCacheState(initListAction)
          .map(cachedState => ({
            initListAction,
            cachedState,
          }))
          .takeUntil(action$.ofType(this.actions.destroyList.TYPE))
      )
      .switchMap(({initListAction, cachedState}) => {
        const parentId = initListAction.payload.parentId;
        return !this.hasRequiredParentId(parentId) ? Observable.empty() : action$.ofType(this.actions.applyFilter.TYPE)
          .map(action => action.payload)
          .distinctUntilChanged(ReduxListEpics.compareFilterData)
          .startWith(getFilterDataFromListState(this._options, cachedState || this._options.getState(store.getState())))
          .switchMap(filterData => {
            const listState = this._options.getState(store.getState());
            const hasMore = listState.hasMore;
            const currentPage = listState.currentPage;
            if (hasMore && this._options.isFilterDataDefault(filterData)) {
              /* No filter - request next page from server */
              let loadPage$ = action$.ofType(this.actions.loadPage.TYPE);

              if (currentPage === 0 && !cachedState)
                loadPage$ = loadPage$.startWith(0);

              return loadPage$
                .map(() => this._options.getState(store.getState()).currentPage)
                .mergeMap(currentPage => this.requestPage(filterData, currentPage, parentId, store.getState()));

            } else if (hasMore) {
              /* With filter - request next page from server */
              return action$.ofType(this.actions.loadPage.TYPE)
                .map(() => this._options.getState(store.getState()).currentFilteredPage)
                .startWith(0)
                .mergeMap(currentPage => this.requestPage(filterData, currentPage, parentId, store.getState()));

            } else {
              /* Everything has been loaded, filter will be applied locally in the reducer only */
              return Observable.empty();
            }
          })
          .takeUntil(action$.ofType(this.actions.destroyList.TYPE))
      })
      .catch(epicErrorHandler(this.logger, 'onLoadPage'));
  }

  /**
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {Store} store
   * @return {Observable<ReduxAction>}
   */
  onDeleteItem(action$, store) {
    return action$.ofType(this.actions.deleteItem.TYPE)
      .distinctUntilChanged(null, action => action.payload.id)
      .mergeMap(action => this.requestDeleteItem(action.payload.id, action, store.getState()))
      .catch(epicErrorHandler(this.logger, 'onDeleteItem'));
  }

  /**
   * @param {String} id - The ID to delete
   * @param {Object} action
   * @param rootState
   * @return {Observable<ReduxAction>}
   */
  requestDeleteItem(id, action, rootState) {
    return this._options.deleteFactory.sendRequest(action.payload, rootState)
      .map(rawResponse => this.actions.deleteItemSuccess({id}, action.meta))
      .catch(expoBackoff(this.logger, 'requestDeleteItem',
        err => this.actions.deleteItemError({id, err, mode: viewModeFromErrorStatus(err.status)}, action.meta),
        (err, errorCount) => errorCount < 5 && isRecoverable(err.status),
      ));
  }

  static compareFilterData(prev, next) {
    return prev.filter === next.filter
      && prev.filterOptions === next.filterOptions
      && prev.sortOptions === next.sortOptions;
  }

  /**
   * @param {?String} parentId
   * @return {Boolean}
   */
  hasRequiredParentId(parentId) {
    return this._options.hasParentId ? !!parentId : true;
  }

  /**
   * @param {FilterData} filterData
   * @param {Number} currentPage
   * @param {?String} parentId
   * @param rootState
   * @return {Observable<ReduxAction<LoadPageSuccessPayload|LoadSuccessPayload|LoadPageError>>}
   */
  requestPage(filterData, currentPage, parentId, rootState) {
    /** @type {ListPageRequest} */
    const listPageRequest = {
      currentPage,
      filterData,
    };
    const meta = {};

    if (this._options.hasParentId) {
      listPageRequest.parentId = parentId;
      meta.parentId = parentId;
    }

    return this._options.pageFactory.sendRequest(listPageRequest, rootState)
      .mergeMap(rawResponse => this.handleListResult(rawResponse, currentPage, meta))
      .catch(expoBackoff(this.logger, 'requestPage',
        err => this.actions.loadPageError({err, mode: viewModeFromErrorStatus(err.status), meta}),
        (err, errorCount) => errorCount < 5 && isRecoverable(err.status),
      ));
  }

  handleListResult(rawResponse, currentPage, meta) {
    const result = this._options.parseListResult(rawResponse, this._options.parseList);
    const successPayload = {
      hasMore: result.hasMore,
      currentPage,
      total: result.total,
    };

    if (result.size === 0) {
      return [this.actions.loadPageSuccess(successPayload, meta)];
    } else {
      return [
        this.actions.loadSuccess({list: result.list}, meta),
        this.actions.loadPageSuccess(successPayload, meta),
      ];
    }
  }

  /**
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {NgStore} store
   * @return {Observable<ReduxAction>}
   */
  onAppReload(action$, store) {
    return this._options.isAppReady$(action$)
      .take(1)
      .mergeMap(() => Observable
        .merge(
          this.createSyncAttempts(store),
          this.triggerSyncList(action$, store),
        ))
      .catch(epicErrorHandler(this.logger, 'onAppReload'));
  }

  /**
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {NgStore} store
   * @return {Observable<ReduxAction>}
   */
  syncModelUpdates(action$, store) {
    const syncModelActionsTypes = this._options.syncModelActionsTypes;
    const online$ = isFunction(this._options.isOnline$) ? this._options.isOnline$(action$) : Observable.of(true);
    const offline$ = isFunction(this._options.isOffline$) ? this._options.isOffline$(action$) : Observable.never();

    return online$
      .mergeMap(() =>
        action$.ofType(...syncModelActionsTypes, this.actions.updateSyncStatus.TYPE)
          .startWith({})
          .throttleTime(this._options.syncModelAttemptThrottle, void 0, {leading: true, trailing: true})
          .takeUntil(offline$)
          .mergeMap(() => this.createSyncAttempts(store))
      )
      .catch(epicErrorHandler(this.logger, 'syncModelUpdates'));
  }

  /**
   * send server requests to attempt syncing each of the items.
   *
   * transform server response into status corresponding update actions
   *
   * if successfully synced
   *   if client matches, update the request status to none
   *   if lastUpdatedByClient matches the request time, remove this id from that list
   *
   * if a network or 500 error occurred
   *   update the request status to pending
   *
   * if a 400 server error occurred
   *   request current valid state and sync, discard local state
   *
   *   use this to cover
   *     malformed requests
   *       need to be careful of api updates here
   *     target resource no longer exists
   *     user no longer has permission to access the target resource
   *     etc
   *
   * @param {NgStore} store
   * @return {Observable<ReduxAction>}
   */
  createSyncAttempts(store) {
    // get items that need to sync and are not in progress already
    const rootState = store.getState();
    const state = this._options.getState(rootState);
    const toSync = this.getModelsToSync(state);

    if (toSync.list.length === 0) {
      return Observable.empty();
    } else {
      return Observable.merge(...toSync.list.map(model => this.startSyncAttempt(model, rootState, toSync.meta)));
    }
  }

  getModelsToSync(state) {
    const list = state.list;
    const result = {
      meta: {},
      list: [],
    };

    forEach(state.updates, (value, itemId) => {
      if (value && value.status === SyncStatus.PENDING && list[itemId]) {
        result.list.push(list[itemId]);
      }
    });

    if (this._options.hasParentId) {
      result.meta.parentId = state.parentId;
    }

    return result;
  }

  startSyncAttempt(model, rootState, meta) {
    const syncTime = Date.now();
    return Observable.merge(
      Observable.of(this.actions.updateSyncStatus({
        id: model.id,
        requestId: model.requestId,
        syncStatus: SyncStatus.IN_PROGRESS,
        syncTime,
      }, meta)),
      this.attemptSync(model, rootState, syncTime, meta),
    );
  }

  /**
   * TODO: add retry logic. Need to fetch latest version of model when retrying...
   * TODO: need to ensure we are not duplicating requests due to simultaneous model update + retry previous model
   * @param model
   * @param rootState
   * @param syncTime
   * @param {Object} meta
   * @param {String} [meta.parentId]
   * @return {*|Observable}
   */
  attemptSync(model, rootState, syncTime, meta) {
    const request = {
      body: this._options.getSyncModelBody(model),
    };

    if (this._options.hasParentId) {
      request.parentId = meta.parentId;
    }

    return this._options.syncModelFactory.sendRequest(request, rootState)
      .map(ajaxResponse => this.actions.updateSyncStatus({
        id: get(ajaxResponse, 'response.id', model.id),
        requestId: model.requestId,
        syncStatus: SyncStatus.SUCCESS,
        syncTime,
        model: ajaxResponse.response,
      }, meta))
      .catch(ajaxError => {
        // TODO: complete error handling logic, add expo backoff retry with takeUtil(offline)
        if (ajaxError.type === 'abort' || this._options.isRecoverable(ajaxError.status)) {
          return Observable.of(this.actions.updateSyncStatus({
            id: model.id,
            requestId: model.requestId,
            syncStatus: SyncStatus.PENDING,
            syncTime,
          }, meta));
        } else {
          this.logger.error({err: ajaxError}, 'Unexpected error in attemptSync');
          // TODO: handle by error status
          return Observable.of(this.actions.updateSyncStatus({
            id: model.id,
            requestId: model.requestId,
            syncStatus: SyncStatus.UNRECOVERABLE,
            syncTime,
          }, meta));
        }
      });
  }
}

function isRecoverable(status) {
  return status === 0 || (status >= 500 && status < 600);
}



// WEBPACK FOOTER //
// ./src/js/_libs/redux-observable-list/src/ReduxListEpics.js